var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { busService } from "../bus";
import * as localforage from "localforage";
import { LayoutUpdatedMessage } from "flavr-common";
const COMPRESSED_CACHE_VALUE_MARKER = "__flavr_cache_compressed_v1";
const MIN_BYTES_FOR_COMPRESSION = 256 * 1024;
const COMPRESS_TEXT_CACHE_VALUES = true;
const COMPRESS_BINARY_CACHE_VALUES = true;
const STORE_BINARY_VALUES_IN_PERSISTENT_CACHE = true;
const MAX_DECOMPRESSION_REQUESTS_IN_FLIGHT = 1;
const DELETE_CACHE_AFTER_DAYS = 28;
const UPDATE_TABLE_AFTER_DAYS = 7;
const UPDATED_TABLE_KEY = "__updated_table";
class CacheService {
    constructor() {
        this.enabled = true;
        this.ignoreFiles = new Set();
        this.loading = new Map();
        this.files = new Map();
        this.storageReads = new Map();
        this.pendingDecompressionRequests = new Map();
        this.queuedDecompressionRequests = [];
        this.activeDecompressionRequests = 0;
        this.nextDecompressionRequestId = 1;
        this.updateTable = new Map();
        this.updateTableDirty = false;
        this.updateTableWhenNeedsUpdateTime = 0;
        this.onSaveUpdateTableTimeout = () => {
            if (!this.updateTableDirty)
                return;
            void this.saveUpdateTable();
        };
        const timeout = setTimeout(() => {
            if (this.storage) {
                const error = new Error("Timeout, no available storage found.");
                this.storage._driverSet = Promise.reject(error);
            }
            this.storage = undefined;
        }, 60000);
        try {
            this.storage = localforage.createInstance({
                name: "flavr_filecache",
                storeName: "flavr_filecache"
            });
            this.storage
                .ready()
                .then(() => {
                if (timeout !== undefined) {
                    clearTimeout(timeout);
                }
            })
                .catch((error) => {
                if (timeout !== undefined) {
                    clearTimeout(timeout);
                }
                console.error("[bus] CacheService", error);
                this.storage = undefined;
            });
        }
        catch (error) {
            if (timeout !== undefined) {
                clearTimeout(timeout);
            }
            console.error("[bus] CacheService", error);
            this.storage = undefined;
        }
        const date = new Date();
        date.setDate(date.getDate() - UPDATE_TABLE_AFTER_DAYS);
        this.updateTableWhenNeedsUpdateTime = date.getTime();
        const connection = busService.connection("CacheService");
        connection.subscribeByType(LayoutUpdatedMessage, () => {
            this.clearMemory();
        });
        this.loadUpdateTable().then(() => {
            void this.removeExpiredCache();
        }).catch(e => { console.error("[cache]", e); });
    }
    clear() {
        return __awaiter(this, void 0, void 0, function* () {
            this.files.clear();
            this.loading.clear();
            this.storageReads.clear();
            this.cancelPendingDecompressionRequests("Cache was cleared");
            if (!this.storage)
                return;
            yield this.storage.clear((error) => {
                if (error) {
                    console.error("[cache] CacheService:clear", error);
                }
            });
        });
    }
    clearMemory() {
        this.files.clear();
        this.loading.clear();
        this.storageReads.clear();
        this.cancelPendingDecompressionRequests("Memory cache was cleared");
    }
    add(key, file, etag) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.enabled)
                return;
            if (this.canIgnore(key))
                return;
            this.files.set(key, {
                data: file,
                etag
            });
            if (!this.storage)
                return;
            const timestamp = Date.now();
            const value = yield this.prepareForStorage(file);
            try {
                yield this.storage.setItem(key, [value, etag, timestamp], (err) => {
                    if (err) {
                        console.error("[cache] CacheService:add", err);
                    }
                });
            }
            catch (error) {
                console.error("[cache] CacheService:add", error);
                return;
            }
            this.addToUpdateTable(key, true, timestamp);
        });
    }
    remove(key) {
        return __awaiter(this, void 0, void 0, function* () {
            this.files.delete(key);
            this.updateTable.delete(key);
            if (!this.storage)
                return;
            yield this.storage.removeItem(key, (err) => {
                if (err) {
                    console.error("[cache] CacheService:remove", err);
                }
            });
        });
    }
    get(key) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.enabled)
                return;
            if (this.canIgnore(key))
                return;
            const cached = this.files.get(key);
            if (cached) {
                return cached.data;
            }
            return (_a = (yield this.loadFromStorage(key, false))) === null || _a === void 0 ? void 0 : _a.data;
        });
    }
    usedCache(key) {
        this.addToUpdateTable(key, false);
    }
    getWithEtag(key_1) {
        return __awaiter(this, arguments, void 0, function* (key, includeStorage = true) {
            if (!this.enabled)
                return;
            if (this.canIgnore(key))
                return;
            const cached = this.files.get(key);
            if (cached) {
                return {
                    data: cached.data,
                    etag: cached.etag,
                    fromMemory: true
                };
            }
            if (!includeStorage)
                return undefined;
            return yield this.loadFromStorage(key, true);
        });
    }
    loadFromStorage(key, withEtag) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.storage) {
                return undefined;
            }
            const inFlight = this.storageReads.get(key);
            if (inFlight) {
                return yield inFlight;
            }
            const promise = (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    const result = yield this.storage.getItem(key, (err, _value) => {
                        if (err) {
                            console.error(withEtag ? "CacheService:getWithEtag" : "CacheService:get", err);
                        }
                    });
                    if (!result || result.length < (withEtag ? 2 : 1)) {
                        return undefined;
                    }
                    if (this.shouldSkipCompressedStorageValue(result[0])) {
                        void this.remove(key);
                        return undefined;
                    }
                    const data = yield this.readFromStorage(result[0]);
                    if (data === undefined) {
                        return undefined;
                    }
                    const etag = withEtag ? result[1] : undefined;
                    this.files.set(key, {
                        data,
                        etag
                    });
                    return {
                        data,
                        etag,
                        fromMemory: false
                    };
                }
                catch (error) {
                    console.error(withEtag ? "CacheService:getWithEtag" : "CacheService:get", error);
                    return undefined;
                }
            }))();
            this.storageReads.set(key, promise);
            try {
                return yield promise;
            }
            finally {
                this.storageReads.delete(key);
            }
        });
    }
    loadText(url, init) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.load(url, "text", init);
        });
    }
    load(url, type, init) {
        return __awaiter(this, void 0, void 0, function* () {
            url = this.resolveURL(url);
            const loadingPromise = this.loading.get(url);
            if (loadingPromise) {
                return yield loadingPromise;
            }
            const promise = this.fetchInternal(url, type, init);
            this.loading.set(url, promise);
            return yield promise;
        });
    }
    fetchInternal(url, type, init) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const request = new Request(url, init);
                const cached = yield this.getWithEtag(url);
                if (cached !== undefined && cached.fromMemory) {
                    return new Response(cached.data, {
                        status: 304
                    });
                }
                if (cached && cached.etag) {
                    request.headers.append("If-None-Match", cached.etag);
                }
                const response = yield fetch(request);
                if (response.status === 304 && cached !== undefined) {
                    this.usedCache(url);
                    return cached.data;
                }
                if (!response.ok) {
                    const etag = (_a = cached === null || cached === void 0 ? void 0 : cached.etag) !== null && _a !== void 0 ? _a : "";
                    throw new Error(`Script could not be loaded: ${url} eTag: ${etag}`);
                }
                const data = yield this.resolveData(response, type);
                if (url && response.ok) {
                    const responseEtag = response.headers.get("etag");
                    yield cacheService.add(url, data, responseEtag);
                }
                return data;
            }
            catch (err) {
                throw new Error((err.message) + url);
            }
            finally {
                this.loading.delete(url);
            }
        });
    }
    resolveURL(input) {
        if (input instanceof URL) {
            return input.href;
        }
        return input;
    }
    resolveData(response, type) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (type) {
                case "arraybuffer":
                    return yield response.arrayBuffer();
                case "blob":
                    return yield response.blob();
                case "json":
                    return yield response.json();
                default:
                    return yield response.text();
            }
        });
    }
    getValueByIndex(array, index) {
        if (!array || array.length < index)
            return undefined;
        const value = array[index];
        if (value === null) {
            return undefined;
        }
        return value;
    }
    canIgnore(key) {
        if (key.startsWith("data:"))
            return true;
        if (key.startsWith("blob:"))
            return true;
        if (key === UPDATED_TABLE_KEY)
            return true;
        return this.ignoreFiles.has(key);
    }
    prepareForStorage(value) {
        return __awaiter(this, void 0, void 0, function* () {
            const compressed = yield this.compressValue(value);
            if (compressed) {
                return compressed;
            }
            return value;
        });
    }
    readFromStorage(value) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.isCompressedCacheValue(value)) {
                return value;
            }
            try {
                const data = yield this.decompressCacheValue(value);
                switch (value.originalType) {
                    case "blob":
                        return new Blob([data], { type: (_a = value.mimeType) !== null && _a !== void 0 ? _a : "" });
                    case "arraybuffer":
                        return data;
                    case "text":
                        return data;
                }
            }
            catch (error) {
                console.error("[cache] CacheService:readFromStorage", error);
                return undefined;
            }
        });
    }
    shouldSkipCompressedStorageValue(value) {
        if (!this.isCompressedCacheValue(value)) {
            return false;
        }
        if (value.originalType === "text") {
            return !COMPRESS_TEXT_CACHE_VALUES;
        }
        return !COMPRESS_BINARY_CACHE_VALUES;
    }
    compressValue(value) {
        return __awaiter(this, void 0, void 0, function* () {
            const compression = this.getCompressionStreamCtor();
            if (!compression)
                return undefined;
            if (value instanceof Blob) {
                if (!COMPRESS_BINARY_CACHE_VALUES)
                    return undefined;
                if (value.size < MIN_BYTES_FOR_COMPRESSION)
                    return undefined;
                const compressedData = yield this.gzipBlob(value, compression);
                if (compressedData.size >= value.size)
                    return undefined;
                return {
                    marker: COMPRESSED_CACHE_VALUE_MARKER,
                    encoding: "gzip",
                    originalType: "blob",
                    compressedData,
                    mimeType: value.type
                };
            }
            if (value instanceof ArrayBuffer) {
                if (!COMPRESS_BINARY_CACHE_VALUES)
                    return undefined;
                const sourceBlob = new Blob([value], { type: "application/octet-stream" });
                if (sourceBlob.size < MIN_BYTES_FOR_COMPRESSION)
                    return undefined;
                const compressedData = yield this.gzipBlob(sourceBlob, compression);
                if (compressedData.size >= sourceBlob.size)
                    return undefined;
                return {
                    marker: COMPRESSED_CACHE_VALUE_MARKER,
                    encoding: "gzip",
                    originalType: "arraybuffer",
                    compressedData,
                    mimeType: sourceBlob.type
                };
            }
            if (typeof value === "string") {
                if (!COMPRESS_TEXT_CACHE_VALUES)
                    return undefined;
                const sourceBlob = new Blob([value], { type: "text/plain;charset=utf-8" });
                if (sourceBlob.size < MIN_BYTES_FOR_COMPRESSION)
                    return undefined;
                const compressedData = yield this.gzipBlob(sourceBlob, compression);
                if (compressedData.size >= sourceBlob.size)
                    return undefined;
                return {
                    marker: COMPRESSED_CACHE_VALUE_MARKER,
                    encoding: "gzip",
                    originalType: "text",
                    compressedData,
                    mimeType: sourceBlob.type
                };
            }
            return undefined;
        });
    }
    isCompressedCacheValue(value) {
        if (!value || typeof value !== "object")
            return false;
        const entry = value;
        if (entry.marker !== COMPRESSED_CACHE_VALUE_MARKER)
            return false;
        if (entry.encoding !== "gzip")
            return false;
        if (!(entry.compressedData instanceof Blob))
            return false;
        return entry.originalType === "blob" || entry.originalType === "arraybuffer" || entry.originalType === "text";
    }
    getCompressionStreamCtor() {
        return globalThis.CompressionStream;
    }
    getDecompressionStreamCtor() {
        return globalThis.DecompressionStream;
    }
    gzipBlob(blob, compression) {
        return __awaiter(this, void 0, void 0, function* () {
            const stream = blob.stream().pipeThrough(new compression("gzip"));
            return yield new Response(stream).blob();
        });
    }
    gunzipBlob(blob) {
        return __awaiter(this, void 0, void 0, function* () {
            const decompression = this.getDecompressionStreamCtor();
            if (!decompression) {
                throw new Error("DecompressionStream is not available.");
            }
            const stream = blob.stream().pipeThrough(new decompression("gzip"));
            return yield new Response(stream).blob();
        });
    }
    decompressCacheValue(value) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof Worker !== "undefined") {
                const worker = this.getDecompressionWorker();
                if (worker) {
                    return yield this.requestDecompression(worker, value.compressedData, value.originalType);
                }
            }
            const blob = yield this.gunzipBlob(value.compressedData);
            if (value.originalType === "text") {
                return yield blob.text();
            }
            return yield blob.arrayBuffer();
        });
    }
    getDecompressionWorker() {
        if (this.decompressionWorker) {
            return this.decompressionWorker;
        }
        if (typeof Worker === "undefined") {
            return undefined;
        }
        try {
            this.decompressionWorker = new Worker(new URL("./CacheDecompressionWorker.ts", import.meta.url));
            this.decompressionWorker.onmessage = (event) => {
                var _a, _b;
                this.activeDecompressionRequests = Math.max(0, this.activeDecompressionRequests - 1);
                this.scheduleDecompressionWork();
                const request = this.pendingDecompressionRequests.get(event.data.id);
                if (!request) {
                    return;
                }
                this.pendingDecompressionRequests.delete(event.data.id);
                if (event.data.success) {
                    request.resolve((_a = event.data.data) !== null && _a !== void 0 ? _a : new ArrayBuffer(0));
                }
                else {
                    request.reject((_b = event.data.error) !== null && _b !== void 0 ? _b : "Decompression failed");
                }
            };
            this.decompressionWorker.onerror = (error) => {
                this.activeDecompressionRequests = 0;
                this.queuedDecompressionRequests.length = 0;
                for (const request of this.pendingDecompressionRequests.values()) {
                    request.reject(error);
                }
                this.pendingDecompressionRequests.clear();
            };
        }
        catch (error) {
            console.error("[cache] CacheService:getDecompressionWorker", error);
            return undefined;
        }
        return this.decompressionWorker;
    }
    requestDecompression(worker, compressedData, originalType) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestId = this.nextDecompressionRequestId++;
            return yield new Promise((resolve, reject) => {
                this.pendingDecompressionRequests.set(requestId, { resolve, reject });
                this.queuedDecompressionRequests.push({
                    id: requestId,
                    compressedData,
                    originalType
                });
                this.scheduleDecompressionWork(worker);
            });
        });
    }
    scheduleDecompressionWork(workerOverride) {
        const worker = workerOverride !== null && workerOverride !== void 0 ? workerOverride : this.decompressionWorker;
        if (!worker) {
            return;
        }
        while (this.activeDecompressionRequests < MAX_DECOMPRESSION_REQUESTS_IN_FLIGHT && this.queuedDecompressionRequests.length > 0) {
            const request = this.queuedDecompressionRequests.shift();
            if (!request) {
                break;
            }
            if (!this.pendingDecompressionRequests.has(request.id)) {
                continue;
            }
            this.activeDecompressionRequests += 1;
            const objectUrl = URL.createObjectURL(request.compressedData);
            try {
                worker.postMessage({ id: request.id, type: "decompress", payload: { url: objectUrl, originalType: request.originalType } });
            }
            catch (error) {
                URL.revokeObjectURL(objectUrl);
                this.activeDecompressionRequests = Math.max(0, this.activeDecompressionRequests - 1);
                const pending = this.pendingDecompressionRequests.get(request.id);
                if (pending) {
                    this.pendingDecompressionRequests.delete(request.id);
                    pending.reject(error);
                }
            }
        }
    }
    cancelPendingDecompressionRequests(reason) {
        this.queuedDecompressionRequests.length = 0;
        this.activeDecompressionRequests = 0;
        for (const request of this.pendingDecompressionRequests.values()) {
            request.reject(new Error(reason));
        }
        this.pendingDecompressionRequests.clear();
    }
    removeExpiredCache() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.storage)
                return;
            try {
                const date = new Date();
                date.setDate(date.getDate() - DELETE_CACHE_AFTER_DAYS);
                const expirationTime = date.getTime();
                let hasChanged = false;
                const table = yield this.storage.getItem(UPDATED_TABLE_KEY);
                const removedFiles = new Set();
                if (table && Array.isArray(table)) {
                    for (const it of table) {
                        if (typeof it.key !== "string") {
                            hasChanged = true;
                            continue;
                        }
                        if (typeof it.timestamp !== "number") {
                            hasChanged = true;
                            continue;
                        }
                        if (it.timestamp < expirationTime) {
                            yield this.remove(it.key);
                            removedFiles.add(it.key);
                            hasChanged = true;
                        }
                        else {
                            // make sure table is uptodate
                            const oldTimestamp = this.updateTable.get(it.key);
                            if (oldTimestamp === undefined || oldTimestamp < it.timestamp) {
                                this.updateTable.set(it.key, it.timestamp);
                            }
                        }
                    }
                }
                else {
                    hasChanged = true;
                }
                if (hasChanged) {
                    yield this.saveUpdateTable(removedFiles);
                }
            }
            catch (error) {
                console.error("removeExpiredCache", error);
            }
        });
    }
    loadUpdateTable() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.storage)
                return;
            try {
                const item = yield this.storage.getItem(UPDATED_TABLE_KEY);
                if (!Array.isArray(item))
                    return;
                for (const it of item) {
                    if (typeof it.key !== "string")
                        continue;
                    if (typeof it.timestamp !== "number")
                        continue;
                    const oldTimestamp = this.updateTable.get(it.key);
                    if (oldTimestamp !== undefined) {
                        if (oldTimestamp > it.timestamp)
                            continue;
                    }
                    this.updateTable.set(it.key, it.timestamp);
                    if (oldTimestamp !== undefined) {
                        if (oldTimestamp > it.timestamp) {
                            this.updateTableDirty = true;
                        }
                    }
                }
                if (this.updateTableDirty) {
                    this.saveUpdateTableAfterTimeout();
                }
            }
            catch (error) {
                console.error("loadUpdateTable", error);
            }
        });
    }
    saveUpdateTable(ignore) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.storage)
                return;
            try {
                this.updateTableDirty = false;
                let item = yield this.storage.getItem(UPDATED_TABLE_KEY);
                const map = new Map();
                if (Array.isArray(item)) {
                    for (const it of item) {
                        if (typeof it.key !== "string")
                            continue;
                        if (typeof it.timestamp !== "number")
                            continue;
                        map.set(it.key, it.timestamp);
                    }
                }
                let changed = false;
                for (const [key, newTimestamp] of this.updateTable) {
                    if (typeof key !== "string")
                        continue;
                    if (typeof newTimestamp !== "number")
                        continue;
                    const oldTimestamp = map.get(key);
                    if (oldTimestamp !== newTimestamp) {
                        changed = true;
                    }
                    map.set(key, newTimestamp);
                }
                if (!changed)
                    return;
                item = [];
                for (const [key, timestamp] of map) {
                    if (ignore && ignore.has(key))
                        continue;
                    item.push({
                        key,
                        timestamp
                    });
                }
                yield this.storage.setItem(UPDATED_TABLE_KEY, item);
            }
            catch (error) {
                console.error("saveUpdateTable", error);
            }
        });
    }
    addToUpdateTable(key, force = false, timestamp) {
        if (timestamp === undefined) {
            timestamp = Date.now();
        }
        let needsSaved = true;
        if (!force) {
            const oldTimestamp = this.updateTable.get(key);
            if (oldTimestamp === undefined) {
                needsSaved = false;
            }
            else if (oldTimestamp > this.updateTableWhenNeedsUpdateTime) {
                return;
            }
        }
        this.updateTable.set(key, timestamp);
        if (needsSaved) {
            this.updateTableDirty = true;
            this.saveUpdateTableAfterTimeout();
        }
    }
    saveUpdateTableAfterTimeout() {
        if (this.saveUpdateTableTimer === undefined) {
            clearTimeout(this.saveUpdateTableTimer);
            this.saveUpdateTableTimer = undefined;
        }
        if (!this.updateTableDirty)
            return;
        this.saveUpdateTableTimer = self.setTimeout(this.onSaveUpdateTableTimeout, 5000);
    }
}
export const cacheService = new CacheService();
